import NotificationService, {
  NOTIFICATION_COUNT_POLL_INTERVAL_MS,
} from "../components/NotificationService";

/** Minimum gap between non-forced refreshes to avoid burst API calls. */
const MIN_REFRESH_GAP_MS = 8000;

let intervalId = null;
let activeUserId = null;
let inFlightRefresh = null;
let lastRefreshAt = 0;
let lastPublishedCount = null;
let visibilityListenerAttached = false;
const subscribers = new Set();

function publish(count) {
  if (count === lastPublishedCount) return;
  lastPublishedCount = count;
  subscribers.forEach((listener) => {
    try {
      listener(count);
    } catch (error) {
      console.error("Notification count subscriber error:", error);
    }
  });
}

function handleVisibilityChange() {
  if (!document.hidden && activeUserId) {
    refreshNotificationCount(activeUserId, { force: true });
  }
}

function attachVisibilityListener() {
  if (visibilityListenerAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerAttached = true;
}

function detachVisibilityListener() {
  if (!visibilityListenerAttached || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerAttached = false;
}

export function subscribeToNotificationCount(listener) {
  subscribers.add(listener);
  if (lastPublishedCount !== null) {
    listener(lastPublishedCount);
  }
  return () => subscribers.delete(listener);
}

/**
 * Fetch unread count. Concurrent calls share one in-flight request.
 * @param {string|number} [userId]
 * @param {{ force?: boolean }} [options] - force=true bypasses throttle (task save, tab focus)
 */
export async function refreshNotificationCount(userId, options = {}) {
  const { force = false } = options;
  const id = userId || NotificationService.getUserId();
  if (!id) return null;

  const now = Date.now();
  if (
    !force &&
    inFlightRefresh === null &&
    now - lastRefreshAt < MIN_REFRESH_GAP_MS &&
    lastPublishedCount !== null
  ) {
    return lastPublishedCount;
  }

  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async () => {
    try {
      const count = await NotificationService.fetchUnreadNotificationCount(id);
      lastRefreshAt = Date.now();
      publish(count);
      return count;
    } catch (error) {
      console.error("Failed to fetch unread notification count:", error);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/** Start 2-minute polling. No-op if already running for the same user. */
export function startNotificationPolling(userId) {
  const id = userId || NotificationService.getUserId();
  if (!id) return;

  if (intervalId && activeUserId === String(id)) {
    return;
  }

  stopNotificationPolling();
  activeUserId = String(id);
  attachVisibilityListener();

  refreshNotificationCount(id, { force: true });
  intervalId = setInterval(() => {
    if (document.hidden) return;
    refreshNotificationCount(id);
  }, NOTIFICATION_COUNT_POLL_INTERVAL_MS);
}

export function stopNotificationPolling() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  activeUserId = null;
  lastRefreshAt = 0;
  lastPublishedCount = null;
  inFlightRefresh = null;
  detachVisibilityListener();
}
